import { useCallback, useRef, useState } from "react";

type CopyOptions = {
  /**
   * Clipboard to write to; this window's by default. UI shown in another
   * window (a popped-out panel) passes that window's: the main document isn't
   * focused there, so a write to its clipboard is rejected.
   */
  clipboard?: Clipboard;
  /** Called after a successful clipboard write. */
  onSuccess?: () => void;
  /** Called if the clipboard write throws (permission denied, no API, etc.). */
  onError?: (error: unknown) => void;
};

/**
 * Clipboard copy with transient "copied" / "failed" flags for button
 * affordances.
 *
 * Each flag flips to `true` on its outcome and auto-resets after `resetMs`
 * (default 2s). Callers own their own messaging via the optional
 * onSuccess/onError callbacks, so this stays UI-agnostic (some call sites toast,
 * some just flip the button label).
 */
export const useCopyToClipboard = (resetMs = 2000) => {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const copy = useCallback(
    async (
      text: string,
      { clipboard = navigator.clipboard, onSuccess, onError }: CopyOptions = {}
    ) => {
      const flash = (outcome: "copied" | "failed") => {
        setCopied(outcome === "copied");
        setFailed(outcome === "failed");
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          setCopied(false);
          setFailed(false);
        }, resetMs);
      };
      try {
        await clipboard.writeText(text);
        flash("copied");
        onSuccess?.();
      } catch (error) {
        flash("failed");
        onError?.(error);
      }
    },
    [resetMs]
  );

  return { copied, failed, copy };
};
